import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  BookOpen,
  Target,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from 'lucide-react'

import { Card, CardContent, Button } from '@/components/ui'
import { setCompletedOnboarding } from '@/lib/onboarding'
import { useAuth } from '@/context/AuthContext'
import { useFamily } from '@/context/FamilyContext'

interface Slide {
  key: string
  icon: React.ReactNode
  title: string
  body: string
  bullets: string[]
}

const slides: Slide[] = [
  {
    key: 'shape',
    icon: <BookOpen className="h-7 w-7" />,
    title: 'Stories Have Shape',
    body: 'Inkwell Jr. helps you build stories that move somewhere on purpose.',
    bullets: [
      'Beginning: meet the hero and the setting',
      'Problem: something blocks what the hero wants',
      'Adventure: tries, surprises, and setbacks',
      'Climax: the biggest moment',
      'Resolution: what changed in the end',
    ],
  },
  {
    key: 'want',
    icon: <Target className="h-7 w-7" />,
    title: 'Characters Want Something',
    body: 'Great stories start when a character wants something and has to work for it.',
    bullets: [
      'Want makes the story move',
      'Fear adds tension',
      'Choices reveal who the hero is',
      'Your Character Cards help you plan it',
    ],
  },
  {
    key: 'revise',
    icon: <RefreshCw className="h-7 w-7" />,
    title: 'Real Writers Revise',
    body: 'First drafts are allowed to be messy. Revision is how stories get good.',
    bullets: [
      'Add detail',
      'Fix confusing parts',
      'Polish a few sentences',
      'Read it out loud and improve it',
    ],
  },
]

export function Onboarding() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { activeWriter } = useFamily()

  const [index, setIndex] = useState(0)

  const isFirst = index === 0
  const isLast = index === slides.length - 1
  const slide = slides[index]

  function goNext() {
    if (!isLast) setIndex((i) => i + 1)
  }

  function goBack() {
    if (!isFirst) setIndex((i) => i - 1)
  }

  function finish() {
    const userId = user?.id
    const childId = activeWriter?.id

    if (!childId && !userId) {
      navigate('/login')
      return
    }

    if (!childId && userId) {
      setCompletedOnboarding({ userId })
      navigate('/select-writer')
      return
    }

    setCompletedOnboarding({ userId: userId ?? undefined, childId: childId ?? undefined })
    navigate('/dashboard')
  }

  function skip() {
    finish()
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[var(--color-kids-bg)]">
      {/* Background decoration */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div
          className="absolute top-20 left-10 w-64 h-64 rounded-full opacity-10"
          style={{ background: 'var(--color-kids-primary)' }}
        />
        <div
          className="absolute bottom-20 right-10 w-48 h-48 rounded-full opacity-10"
          style={{ background: 'var(--color-kids-secondary)' }}
        />
      </div>

      <Card className="w-full max-w-2xl">
        <CardContent className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[var(--color-kids-primary)]" />
              <span className="text-sm font-medium text-[var(--color-kids-text-muted)]">
                Step {index + 1} of {slides.length}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={skip}>
              Skip
            </Button>
          </div>

          {/* Slide content */}
          <div className="flex items-start gap-4 mb-6">
            <div
              className="h-14 w-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'var(--color-kids-surface-alt)' }}
            >
              <div className="text-[var(--color-kids-primary)]">{slide.icon}</div>
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-[var(--color-kids-text)]">
                {slide.title}
              </h1>
              <p className="mt-1 text-[var(--color-kids-text-muted)]">{slide.body}</p>
            </div>
          </div>

          {/* Animated bullets */}
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="rounded-2xl bg-[var(--color-kids-surface-alt)] p-6"
            >
              <ul className="space-y-3">
                {slide.bullets.map((bullet) => (
                  <li
                    key={bullet}
                    className="flex gap-3 items-start text-[var(--color-kids-text)]"
                  >
                    <span className="text-[var(--color-kids-primary)] mt-1">•</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="mt-8 flex items-center justify-between">
            <Button variant="secondary" onClick={goBack} disabled={isFirst}>
              <ChevronLeft className="h-5 w-5 mr-1" />
              Back
            </Button>

            {/* Progress dots */}
            <div className="flex items-center gap-2">
              {slides.map((s, i) => (
                <div
                  key={s.key}
                  className={`h-2.5 w-2.5 rounded-full transition-colors ${
                    i === index
                      ? 'bg-[var(--color-kids-primary)]'
                      : 'bg-[var(--color-kids-surface-alt)]'
                  }`}
                />
              ))}
            </div>

            {isLast ? (
              <Button onClick={finish}>
                Start Writing
                <ChevronRight className="h-5 w-5 ml-1" />
              </Button>
            ) : (
              <Button onClick={goNext}>
                Next
                <ChevronRight className="h-5 w-5 ml-1" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
